import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { GbeEndPointV4 } from '../models/gbe/gbe-endpoint.enum';
import { GBE_BASE_URL } from '../tokens/gbe-url.token';
import { convertKeysToSnakeCase, urlMatchesEndpoint } from '../utils/case-conversions';

export const apiCaseRequestInterceptor: HttpInterceptorFn = (req, next) => {
  // add other apis if needed
  const gbeBaseUrl = inject(GBE_BASE_URL);
  const snakeCaseApis: string[] = [gbeBaseUrl];
  const ignoredKeysPerUrl = new Map<string, string[]>();

  // we don't want to temper with the original json schemas returned by GBE
  const transformerUrl = gbeBaseUrl + '/' + GbeEndPointV4.TRANSFORMERS;
  ignoredKeysPerUrl.set(transformerUrl, ['data']);

  // the data endpoint carries raw, user-defined entity data; its `data` keys
  // must be sent verbatim and never snake_cased.
  const dataUrl = gbeBaseUrl + '/' + GbeEndPointV4.DATA;
  ignoredKeysPerUrl.set(dataUrl, ['data']);

  // plan-scoped entity writes (v4/plans/{id}/data/{type}) carry the same raw
  // entity `data` payload; exempt it so uids like `PAL_SOL_SE` are not snake_cased.
  const plansUrl = gbeBaseUrl + '/' + GbeEndPointV4.PLANS;
  ignoredKeysPerUrl.set(plansUrl, ['data']);

  // GFE preferences are a free-form blob stored verbatim by GBE; keep its keys
  // untouched so the camelCase payload round-trips unchanged.
  const userPreferencesUrl = gbeBaseUrl + '/' + GbeEndPointV4.USER_PREFERENCES;
  ignoredKeysPerUrl.set(userPreferencesUrl, ['preferences']);

  // The dataspace wizard inlines the same parameter `data` blobs in its
  // full-create payload.
  // `entity_versions.entities` is exempt for a different reason: GBE validates
  // it with the entity-version ref DTO borrowed from the data-layer module,
  // whose properties are camelCase (`entityType`), and the full-create route
  // has no camel-case middleware to translate them. Snake-casing those refs
  // makes the back end reject the whole wizard with `entity_type must be a
  // string`.
  const fullCreateDataspaceUrl = gbeBaseUrl + '/' + GbeEndPointV4.DATASPACES_FULL_CREATE;
  ignoredKeysPerUrl.set(fullCreateDataspaceUrl, ['data', 'entities']);

  // A solver parameter set's `data` is a free-form tuning blob stored verbatim
  // by GBE and authored as raw JSON in the admin editor; snake_casing it would
  // rewrite keys the admin typed (`MAX_TIME` -> `m_a_x__t_i_m_e`).
  const solverParametersUrl = gbeBaseUrl + '/' + GbeEndPointV4.SOLVER_PARAMETERS;
  ignoredKeysPerUrl.set(solverParametersUrl, ['data']);

  // A recipe's `filters` is a free-form filter configuration stored verbatim by
  // GBE and authored as raw JSON in the admin editor, so it goes out as typed.
  const recipesUrl = gbeBaseUrl + '/' + GbeEndPointV4.RECIPES;
  ignoredKeysPerUrl.set(recipesUrl, ['filters']);

  const isTargeted = snakeCaseApis.some((snakeCaseUrl) => req.url.startsWith(snakeCaseUrl));

  if (!isTargeted) {
    return next(req);
  }

  const ignoredKeys =
    [...ignoredKeysPerUrl.entries()].find(([url]) =>
      urlMatchesEndpoint(req.url, url)
    )?.[1] ?? [];

  return next(
    req.clone({
      body: convertKeysToSnakeCase(req.body, ignoredKeys)
    })
  );
};
